<template>
    <div class="h-100">
        <b-container class="p-0" fluid>
            <b-row align-h="center">
                <b-col cols="12" md="10">
                    <b-link :to="{ name: 'settings' }">
                        <font-awesome-icon icon="arrow-left" />
                        {{ $t("Back") }}
                    </b-link>
                    <h1 class="mt-1 mb-2">
                        {{ $t("mainmenu.settings") }} /
                        {{ $t("settings.users.Users") }}
                    </h1>

                    <div v-if="!isLoading" class="mb-2">
                        <b-row align-h="between">
                            <b-col class="d-flex align-items-baseline" cols="12" md="6">
                                <b-button
                                    v-if="$can('create', 'users')"
                                    id="contacts-add"
                                    class="mr-2 text-nowrap"
                                    variant="primary"
                                    @click="addUserOrOpenRequestModal($bvModal)"
                                >
                                    <font-awesome-icon class="mr-50" icon="plus" />
                                    <span>{{ $t("settings.users.Add user") }}</span>
                                </b-button>

                                <div class="d-flex align-items-center justify-content-end">
                                    <b-form-input
                                        v-model="searchQuery"
                                        :placeholder="$t('paginator.search')"
                                        class="d-inline-block mr-1 bg-light"
                                        debounce="500"
                                    />
                                </div>

                                <user-limits />
                            </b-col>

                            <b-col cols="auto">
                                <label>{{ $t("paginator.show") }}</label>
                                <v-select
                                    v-model="perPage"
                                    :clearable="false"
                                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                    :options="perPageOptions"
                                    class="per-page-selector d-inline-block mx-50"
                                />
                                <label>{{ $t("paginator.entries") }}</label>
                            </b-col>
                        </b-row>
                    </div>

                    <b-table
                        ref="refTable"
                        :empty-text="$t('paginator.no-matching-records-found')"
                        :fields="tableColumns"
                        :items="read"
                        :no-sort-reset="true"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="isSortDirDesc"
                        :table-busy="isLoading"
                        class="position-relative"
                        primary-key="id"
                        responsive
                        show-empty
                    >
                        <template #table-busy>
                            <div class="d-flex justify-content-center my-1">
                                <b-spinner />
                            </div>
                        </template>

                        <!-- Column: Name -->
                        <template #cell(name)="data">
                            <b-avatar
                                :src="data.item.pictureUrl"
                                :text="resolveInitials(data.item.name)"
                                class="no-border badge-minimal avatar-border-2 mr-1 round-large"
                                size="md"
                                variant="tertiary"
                            />

                            <strong>{{ data.item.name }}</strong>
                        </template>

                        <!-- Column: Active -->
                        <template #cell(active)="data">
                            <b-form-checkbox
                                :key="data.item.id"
                                v-model="data.item.active"
                                v-b-tooltip="resolveActiveTooltip(data.item)"
                                :disabled="data.item.id === getUserData().id || !data.item.lastLogin"
                                inline
                                name="active"
                                switch
                                unchecked-value="0"
                                value="1"
                                @change="
                                    toggleActive(
                                        {
                                            userId: data.item.id,
                                            name: data.item.name,
                                            currentState: data.item.active,
                                        },
                                        $event
                                    )
                                "
                            >
                            </b-form-checkbox>
                        </template>

                        <template #cell(teams)="data">
                            <b-badge
                                v-for="team in data.item.teams.slice(0, 2)"
                                :key="team.id"
                                :style="{
                                    'background-color': team.color,
                                    color: textColorByBackgroundColor(team.color),
                                }"
                                class="mr-1"
                            >
                                {{ team.name }}
                            </b-badge>
                            <b-badge
                                v-if="data.item.teams.length > 2"
                                v-b-tooltip.hover.top="
                                    data.item.teams
                                        .slice(2)
                                        .map((team) => team.name)
                                        .join(', ')
                                "
                                variant="light text-secondary"
                                >+ {{ data.item.teams.length - 2 }}
                            </b-badge>
                        </template>
                        <!-- Column: Actions -->
                        <template #cell(actions)="data">
                            <div class="d-flex align-items-center justify-content-end flex-nowrap">
                                <span v-b-tooltip="resolveInviteTooltip(data.item)" class="wrapper mr-50">
                                    <b-button
                                        :disabled="Boolean(data.item.lastLogin)"
                                        class="btn-icon"
                                        variant="light text-secondary"
                                        @click="invite(data.item.email)"
                                    >
                                        <font-awesome-icon icon="envelope" />
                                    </b-button>
                                </span>
                                <span v-b-tooltip="resolveCopyInviteTooltip(data.item)" class="wrapper mr-50">
                                    <b-button
                                        :disabled="Boolean(data.item.lastLogin)"
                                        class="btn-icon"
                                        variant="light text-secondary"
                                        @click="copyInviteLink(data.item.id)"
                                    >
                                        <font-awesome-icon icon="copy" />
                                    </b-button>
                                </span>
                                <b-button
                                    v-if="$can('update', 'users')"
                                    :to="{
                                        name: 'settings-users-edit',
                                        params: { id: data.item.id },
                                    }"
                                    class="btn-icon mr-50"
                                    variant="light text-secondary"
                                >
                                    <font-awesome-icon icon="pen" />
                                </b-button>
                                <span v-b-tooltip="$t(resolveDeleteTooltip(data.item))" class="wrapper">
                                    <b-button
                                        v-if="$can('delete', 'users')"
                                        :disabled="data.item.id === getUserData().id"
                                        class="btn-icon"
                                        variant="light text-secondary"
                                        @click="deleteUser(data.item.id, data.item.name)"
                                    >
                                        <font-awesome-icon icon="trash" />
                                    </b-button>
                                </span>
                            </div>
                        </template>
                    </b-table>
                    <div v-if="!isLoading" class="mx-2 mb-2">
                        <b-row>
                            <b-col
                                class="d-flex align-items-center justify-content-center justify-content-sm-start"
                                cols="12"
                                sm="6"
                            >
                                <span class="text-muted">
                                    {{ $t("paginator.showing") }} {{ dataMeta.to }} {{ $t("paginator.of") }}
                                    {{ dataMeta.of }} {{ $t("paginator.entries") }}
                                </span>
                            </b-col>

                            <b-col
                                class="d-flex align-items-center justify-content-center justify-content-sm-end"
                                cols="12"
                                sm="6"
                            >
                                <b-pagination
                                    v-model="currentPage"
                                    :per-page="perPage"
                                    :total-rows="total"
                                    class="mb-0 mt-1 mt-sm-0"
                                    first-number
                                    last-number
                                    next-class="next-item"
                                    prev-class="prev-item"
                                    size="sm"
                                >
                                    <template #prev-text>
                                        <font-awesome-icon icon="chevron-left" size="lg" />
                                    </template>
                                    <template #next-text>
                                        <font-awesome-icon icon="chevron-right" size="lg" />
                                    </template>
                                </b-pagination>
                            </b-col>
                        </b-row>
                    </div>
                </b-col>
            </b-row>
        </b-container>
        <extra-users-request-modal />
    </div>
</template>

<script>
import { getUserData } from "@/auth/getUserData";
import store from "@/store";
import { textColorByBackgroundColor } from "@/utils/color";
import { resolveInitials } from "@/utils/users";
import ExtraUsersRequestModal from "@/views/modules/settings/users/extraUserRequestModal.vue";
import UserLimits from "@/views/modules/settings/users/userLimits.vue";
import {
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCardText,
    BCol,
    BContainer,
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
    BFormInput,
    BLink,
    BPagination,
    BRow,
    BSpinner,
    BTab,
    BTable,
    BTabs,
    VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { useToast } from "vue-toastification/composition";
// @ts-ignore
import i18n from "@/libs/i18n";
import useUsers from "./useUsers";
import useUsersList from "./useUsersList";
import axios from "@/libs/axios";

export default {
    components: {
        BBadge,
        BAvatar,
        BContainer,
        BFormCheckbox,
        BCard,
        BRow,
        BCol,
        BFormInput,
        BButton,
        BTable,
        BDropdown,
        BDropdownItem,
        BPagination,
        BCardText,
        BTabs,
        BTab,
        BLink,
        BSpinner,
        vSelect,
        UserLimits,
        ExtraUsersRequestModal,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    setup(_, context) {
        const bvModal = context.root.$bvModal;
        const toast = useToast();
        const {
            read,
            refetchData,
            tableColumns,
            perPage,
            currentPage,
            total,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            refTable,
            isLoading,
        } = useUsersList();

        const { sendInviteEmail, getInviteLink, addUserOrOpenRequestModal } = useUsers();
        const resolveDeleteTooltip = (user) => {
            if (user.id === getUserData().id) {
                return "settings.users.cant-delete-self";
            }
            return "";
        };
        const resolveActiveTooltip = (user) => {
            if (user.id === getUserData().id) {
                return i18n.t("settings.users.cant-deactivate-self");
            }

            if (!user.lastLogin) {
                return i18n.t("settings.users.cant-activate-uninitialized-user");
            }
        };

        const resolveInviteTooltip = (user) => {
            if (user.lastLogin) {
                return i18n.t("settings.users.cant-invite-active-user");
            }
        };

        const resolveCopyInviteTooltip = (user) => {
            if (user.lastLogin) {
                return i18n.t("settings.users.cant-invite-active-user");
            }
            return i18n.t("settings.users.copy-invite-link.tooltip");
        };

        const invite = (email) => {
            sendInviteEmail(email)
                .then(() => {
                    toast.success(i18n.t("settings.users.sending-email.success"));
                })
                .catch(() => {
                    toast.error(i18n.t("settings.users.sending-email.error"));
                });
        };

        const copyInviteLink = (userId) => {
            getInviteLink(userId)
                .then((url) => context.root.$copyText(url))
                .then(() => {
                    toast.success(i18n.t("settings.users.copy-invite-link.success"));
                })
                .catch(() => {
                    toast.error(i18n.t("settings.users.copy-invite-link.error"));
                });
        };
        const toggleActive = ({ userId, name, currentState }, newState) => {
            store
                .dispatch("users/update", {
                    id: userId,
                    active: Number(newState),
                })
                .then(() => {
                    toast.success(i18n.t("toast.update.success", { entity: name }));
                })
                .catch(() => {
                    toast.error(i18n.t("toast.update.error", { entity: name }));
                });
        };

        const deleteUser = (id) => {
            bvModal
                .msgBoxConfirm(i18n.t("confirm.Are you sure?"), {
                    title: i18n.t("confirm.Are you sure?"),
                    cancelTitle: i18n.t("confirm.Cancel"),
                    cancelVariant: "light",
                    okTitle: i18n.t("confirm.Yes, delete"),
                    hideHeaderClose: false,
                    headerCloseVariant: "light",
                    centered: true,
                })
                .then((result) => {
                    if (result) {
                        axios
                            .delete(`users/${id}`)
                            .then(() => {
                                refetchData();

                                toast.success(i18n.t("alert.delete.success.title"));
                            })
                            .catch(() => {
                                toast.error(i18n.t("alert.delete.error.title"));
                            });
                    }
                });
        };

        return {
            read,
            refetchData,
            tableColumns,
            perPage,
            currentPage,
            total,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            refTable,
            isLoading,
            sendInviteEmail,
            getUserData,
            toast,
            resolveInitials,
            resolveDeleteTooltip,
            addUserOrOpenRequestModal,
            textColorByBackgroundColor,
            resolveActiveTooltip,
            resolveInviteTooltip,
            resolveCopyInviteTooltip,
            invite,
            copyInviteLink,
            toggleActive,
            deleteUser,
        };
    },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.no-border {
    img {
        border: none !important;
    }
}
</style>
